import * as React from 'react';
import { CalendarDays } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Button } from '../button';
import { Input } from '../input';
import { FormField } from '../form-field';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

/**
 * Canonical day-granularity range control: N-day presets plus a custom
 * calendar range.
 *
 * Lifted verbatim in behaviour from `features/finance/refunds/refunds-filters.tsx`,
 * which was the only surface in Atlas that got this right — every other chart
 * either had presets with no custom option, or a custom option that filtered
 * client-side over a fixed fetch window (so a range outside that window
 * silently returned the wrong answer). Consumers are expected to feed
 * `from`/`to` straight into the query key and the RPC arguments.
 *
 * `maxSpanDays` exists because a custom range is the one control a user can
 * point at an arbitrarily long window, and the `authenticated` role has an 8s
 * statement timeout. Bound it at the call site to whatever the backing RPC
 * actually accepts rather than letting the request 500.
 *
 * Dates are plain `YYYY-MM-DD` day strings, not timestamps: the surfaces that
 * use this read daily rollups keyed on an Asia/Dubai calendar date, and
 * introducing a client timezone here would shift the boundary day.
 */

export interface DayRange {
  /** Preset key, or 'custom' when from/to were picked by hand. */
  preset: string;
  /** Inclusive start, `YYYY-MM-DD`. */
  from: string;
  /** Inclusive end, `YYYY-MM-DD`. */
  to: string;
}

export interface DayRangePreset {
  key: string;
  label: string;
  /** Window length in days, inclusive of both endpoints. */
  days: number;
}

export const DEFAULT_DAY_RANGE_PRESETS: readonly DayRangePreset[] = [
  { key: '7d', label: '7d', days: 7 },
  { key: '30d', label: '30d', days: 30 },
  { key: '90d', label: '90d', days: 90 },
];

export function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Shape check alone is not enough: `Date.parse('2026-02-31')` succeeds and
 * normalises to March 3, so a hand-edited URL would silently query a different
 * range than the one it names. Round-trip the parsed date and require the
 * components to survive.
 */
export function isValidIsoDay(v: unknown): v is string {
  if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const [y, m, d] = v.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m! - 1 && dt.getUTCDate() === d;
}

/** Inclusive day span between two `YYYY-MM-DD` strings. `7d` spans 7. */
export function daySpan(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
}

/**
 * Build the range for a preset.
 *
 * `anchor` is the day the window ends on, and it is not always today: a
 * surface reading a nightly rollup ends its presets on yesterday, because
 * today's row is still being written and including it would show a partial
 * day next to complete ones.
 */
export function rangeForPreset(
  key: string,
  presets: readonly DayRangePreset[] = DEFAULT_DAY_RANGE_PRESETS,
  anchor: Date | string = new Date(),
): DayRange {
  const end =
    typeof anchor === 'string'
      ? anchor
      : isoDay(new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate()));
  const days = presets.find((p) => p.key === key)?.days ?? 30;
  const [y, m, d] = end.split('-').map(Number);
  const start = new Date(Date.UTC(y!, m! - 1, d!));
  start.setUTCDate(start.getUTCDate() - (days - 1));
  return {
    preset: key,
    from: `${start.getUTCFullYear()}-${String(start.getUTCMonth() + 1).padStart(2, '0')}-${String(start.getUTCDate()).padStart(2, '0')}`,
    to: end,
  };
}

/**
 * Recover which preset (if any) a raw from/to pair corresponds to, so a range
 * restored from the URL renders the matching button as pressed instead of
 * always falling through to "Custom". A historical window of exactly the right
 * length is NOT a preset — a preset always ends on the anchor day, so a shared
 * link carrying months-old absolute dates reads as Custom rather than lighting
 * up "30d" while querying a stale window.
 */
export function presetForRange(
  from: string,
  to: string,
  presets: readonly DayRangePreset[] = DEFAULT_DAY_RANGE_PRESETS,
  anchor: Date | string = new Date(),
): string {
  const anchorDay =
    typeof anchor === 'string'
      ? anchor
      : isoDay(new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate()));
  if (to !== anchorDay) return 'custom';
  return presets.find((p) => p.days === daySpan(from, to))?.key ?? 'custom';
}

export interface DateRangeControlProps {
  value: DayRange;
  onChange: (next: DayRange) => void;
  presets?: readonly DayRangePreset[];
  /**
   * Longest custom window the backing query can serve. A range wider than this
   * is rejected in the popover with the reason shown, rather than being sent
   * and timing out.
   */
  maxSpanDays?: number;
  /**
   * Day the presets end on, `YYYY-MM-DD`. Defaults to today. Set to yesterday
   * on surfaces backed by a nightly rollup whose row for today is still being
   * written. Also becomes the default calendar ceiling.
   */
  anchorDay?: string;
  /** Latest selectable day. Defaults to `anchorDay`. */
  maxDay?: string;
  /** Accessible name for the preset group. */
  label?: string;
  className?: string;
}

export function DateRangeControl({
  value,
  onChange,
  presets = DEFAULT_DAY_RANGE_PRESETS,
  maxSpanDays,
  anchorDay,
  maxDay,
  label = 'Date range',
  className,
}: DateRangeControlProps) {
  // FormField now clones its single child to fill in `id`/`aria-describedby`
  // when the caller doesn't set one (see form-field.tsx), so these explicit
  // ids are no longer load-bearing for the label association — kept anyway
  // for stable, predictable ids on a widely-reused control rather than
  // falling back to FormField's generated ones.
  const fromId = React.useId();
  const toId = React.useId();
  const [open, setOpen] = React.useState(false);
  const [draftFrom, setDraftFrom] = React.useState(value.from);
  const [draftTo, setDraftTo] = React.useState(value.to);

  const anchor = anchorDay ?? isoDay(new Date());
  const ceiling = maxDay ?? anchor;

  const [orderedFrom, orderedTo] =
    draftFrom && draftTo && draftFrom > draftTo ? [draftTo, draftFrom] : [draftFrom, draftTo];

  const span = orderedFrom && orderedTo ? daySpan(orderedFrom, orderedTo) : 0;
  const tooWide = maxSpanDays != null && span > maxSpanDays;
  // The input's `max` attribute marks a later date invalid but does not stop an
  // ordinary button from firing, so a typed or pasted future date would sail
  // through presence+span checks. The hub would then store a future range in
  // the URL while its RPC clamps to yesterday, and the on-hire RPC would return
  // a short series with nothing saying which dates were missing.
  const beyondCeiling = Boolean(orderedTo) && orderedTo > ceiling;
  const canApply = Boolean(orderedFrom && orderedTo) && !tooWide && !beyondCeiling;

  const applyCustom = () => {
    if (!canApply) return;
    onChange({ preset: 'custom', from: orderedFrom, to: orderedTo });
    setOpen(false);
  };

  const customLabel = value.preset === 'custom' ? `${value.from.slice(5)} → ${value.to.slice(5)}` : 'Custom';

  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        'inline-flex flex-wrap items-center gap-0.5 rounded-md border border-border bg-bg-surface p-0.5',
        className,
      )}
    >
      {presets.map((p) => (
        <button
          key={p.key}
          type="button"
          aria-pressed={value.preset === p.key}
          onClick={() => onChange(rangeForPreset(p.key, presets, anchor))}
          className={cn(
            // The BUTTON is 44x44, not a pseudo-element pretending to be.
            //
            // accessibility.md:99 allows extending the hit area past a smaller
            // visual, and two attempts at that here were both wrong: the first
            // set height only, and the second centred a 44px-wide ::before over
            // a ~32-39px button separated by a 2px gap, so adjacent overlays
            // overlapped and the later sibling painted over its predecessor —
            // a tap near an edge activated the neighbouring range. On a
            // segmented control the overlay approach cannot work without
            // z-index bookkeeping nobody will maintain.
            //
            // Sizing the real element removes the failure mode by
            // construction: hit area equals visual, no stacking order to get
            // wrong, and one less mechanism in a shared component. 44px is an
            // ordinary control height; the segmented group just reads slightly
            // chunkier than it did.
            'inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded px-2.5',
            'text-caption font-medium transition-colors',
            value.preset === p.key
              ? 'bg-accent-500 text-text-on-brand'
              : 'text-text-secondary hover:bg-bg-inset',
          )}
        >
          {p.label}
        </button>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-pressed={value.preset === 'custom'}
            className={cn(
              // Same reasoning as the presets above — real 44px element, no
              // overlay.
              'inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1 rounded px-2.5',
              'text-caption font-medium transition-colors',
              value.preset === 'custom'
                ? 'bg-accent-500 text-text-on-brand'
                : 'text-text-secondary hover:bg-bg-inset',
            )}
            onClick={() => {
              setDraftFrom(value.from);
              setDraftTo(value.to);
            }}
          >
            <CalendarDays className="h-3 w-3" aria-hidden />
            <span>{customLabel}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-4">
          <div className="mb-3 text-caption font-medium text-text-primary">Custom range</div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="From" htmlFor={fromId}>
              <Input
                id={fromId}
                type="date"
                value={draftFrom}
                max={draftTo || ceiling}
                onChange={(e) => setDraftFrom(e.target.value)}
              />
            </FormField>
            <FormField label="To" htmlFor={toId}>
              <Input
                id={toId}
                type="date"
                value={draftTo}
                min={draftFrom || undefined}
                max={ceiling}
                onChange={(e) => setDraftTo(e.target.value)}
              />
            </FormField>
          </div>
          {tooWide || beyondCeiling ? (
            <p role="alert" className="mt-3 text-caption" style={{ color: 'var(--danger-text)' }}>
              {beyondCeiling
                ? `There is no data after ${ceiling}. Pick an earlier end date.`
                : `${span.toLocaleString()} days is wider than this chart can serve (${maxSpanDays!.toLocaleString()} days max). Narrow the range.`}
            </p>
          ) : null}
          <div className="mt-3 flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={applyCustom} disabled={!canApply}>
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
